/**
 * Enterprise-grade error handling utility
 * 
 * Provides centralized error handling with:
 * - Structured logging
 * - User-friendly error messages
 * - Error classification
 * - Retry logic support
 * - Error reporting integration
 */

import { logger } from './logger';
import type { ApiError } from '@/services/api';

export interface ErrorContext {
  operation?: string;
  entity?: string;
  entityId?: string;
  userId?: string;
  correlationId?: string;
  [key: string]: unknown;
}

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

export enum ErrorCategory {
  NETWORK = 'network',
  VALIDATION = 'validation',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  NOT_FOUND = 'not_found',
  SERVER_ERROR = 'server_error',
  CLIENT_ERROR = 'client_error',
  UNKNOWN = 'unknown',
}

export interface HandledError {
  message: string;
  userMessage: string;
  category: ErrorCategory;
  severity: ErrorSeverity;
  statusCode?: number;
  retryable: boolean;
  originalError: unknown;
}

/**
 * Classify error based on status code and error type
 */
function classifyError(error: ApiError | Error | unknown): {
  category: ErrorCategory;
  severity: ErrorSeverity;
  retryable: boolean;
} {
  const apiError = error as ApiError;
  const status = apiError?.status;

  if (!status) {
    // Network errors or unknown errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return {
        category: ErrorCategory.NETWORK,
        severity: ErrorSeverity.MEDIUM,
        retryable: true,
      };
    }
    return {
      category: ErrorCategory.UNKNOWN,
      severity: ErrorSeverity.MEDIUM,
      retryable: false,
    };
  }

  // 4xx Client Errors
  if (status >= 400 && status < 500) {
    if (status === 401) {
      return {
        category: ErrorCategory.AUTHENTICATION,
        severity: ErrorSeverity.HIGH,
        retryable: false,
      };
    }
    if (status === 403) {
      return {
        category: ErrorCategory.AUTHORIZATION,
        severity: ErrorSeverity.HIGH,
        retryable: false,
      };
    }
    if (status === 404) {
      return {
        category: ErrorCategory.NOT_FOUND,
        severity: ErrorSeverity.LOW,
        retryable: false,
      };
    }
    if (status === 429) {
      return {
        category: ErrorCategory.CLIENT_ERROR,
        severity: ErrorSeverity.MEDIUM,
        retryable: true, // Rate limit errors can be retried after waiting
      };
    }
    if (status === 422 || status === 400) {
      return {
        category: ErrorCategory.VALIDATION,
        severity: ErrorSeverity.MEDIUM,
        retryable: false,
      };
    }
    return {
      category: ErrorCategory.CLIENT_ERROR,
      severity: ErrorSeverity.MEDIUM,
      retryable: false,
    };
  }

  // 5xx Server Errors
  if (status >= 500) {
    return {
      category: ErrorCategory.SERVER_ERROR,
      severity: ErrorSeverity.HIGH,
      retryable: true,
    };
  }

  return {
    category: ErrorCategory.UNKNOWN,
    severity: ErrorSeverity.MEDIUM,
    retryable: false,
  };
}

/**
 * Generate user-friendly error message
 */
function getUserMessage(
  error: ApiError | Error | unknown,
  category: ErrorCategory,
  context?: ErrorContext
): string {
  const apiError = error as ApiError;
  const status = apiError?.status;
  const operation = context?.operation || 'operation';
  const entity = context?.entity || 'item';

  // Use error message from API if available and meaningful
  // Prefer API message over generic messages for 400 (Business Error) and 422 (Validation Error)
  if (apiError?.message && 
      !apiError.message.includes('Unexpected error') &&
      !apiError.message.includes('Failed to fetch') &&
      apiError.message.trim().length > 0) {
    // For validation and business errors (400, 422), always use the API message
    if (status === 400 || status === 422) {
      return apiError.message;
    }
    // For other errors, use API message if it seems user-friendly
    if (status !== 500 && status !== 503 && status !== 502) {
      return apiError.message;
    }
  }

  // Category-specific messages (fallback when API message is not available)
  switch (category) {
    case ErrorCategory.NETWORK:
      return `Unable to connect to the server. Please check your internet connection and try again.`;
    case ErrorCategory.AUTHENTICATION:
      return `Your session has expired. Please log in again.`;
    case ErrorCategory.AUTHORIZATION:
      return `You don't have permission to ${operation} this ${entity}.`;
    case ErrorCategory.NOT_FOUND:
      return `The ${entity} you're looking for doesn't exist or has been removed.`;
    case ErrorCategory.VALIDATION:
      // For validation errors, prefer API message if available, otherwise use generic
      return apiError?.message && apiError.message !== 'Unexpected error'
        ? apiError.message
        : `Please check your input and try again.`;
    case ErrorCategory.SERVER_ERROR:
      return `A server error occurred. Please try again in a few moments.`;
    case ErrorCategory.CLIENT_ERROR:
      if (status === 400) {
        // For 400 errors, prefer API message if available
        return apiError?.message && apiError.message !== 'Unexpected error'
          ? apiError.message
          : `Invalid request. Please check your input and try again.`;
      }
      if (status === 429) {
        // Check if retry timing is available in error details
        const apiError = error as ApiError;
        const retryAfter = (apiError?.details as { retryAfter?: number })?.retryAfter;
        if (retryAfter && retryAfter > 0) {
          const retryTime = retryAfter < 60 
            ? `${retryAfter} second${retryAfter !== 1 ? 's' : ''}`
            : `${Math.ceil(retryAfter / 60)} minute${Math.ceil(retryAfter / 60) !== 1 ? 's' : ''}`;
          return `Rate limit exceeded. Please try again in ${retryTime}.`;
        }
        return `Rate limit exceeded. Please wait a moment and try again.`;
      }
      return `An error occurred while ${operation} the ${entity}. Please try again.`;
    default:
      return `An unexpected error occurred. Please try again or contact support if the problem persists.`;
  }
}

/**
 * Handle and process errors with logging and user-friendly messages
 */
export function handleError(
  error: unknown,
  context?: ErrorContext
): HandledError {
  const apiError = error as ApiError;
  const { category, severity, retryable } = classifyError(error);

  // Extract error message
  const errorMessage =
    apiError?.message ||
    (error instanceof Error ? error.message : 'An unexpected error occurred');

  // Generate user-friendly message
  const userMessage = getUserMessage(error, category, context);

  // Create handled error object
  const handledError: HandledError = {
    message: errorMessage,
    userMessage,
    category,
    severity,
    statusCode: apiError?.status,
    retryable,
    originalError: error,
  };

  // Log error with context
  const logContext: ErrorContext = {
    ...context,
    errorCategory: category,
    errorSeverity: severity,
    statusCode: apiError?.status,
    retryable,
  };

  if (severity === ErrorSeverity.CRITICAL || severity === ErrorSeverity.HIGH) {
    logger.error(
      `Error in ${context?.operation || 'operation'}: ${errorMessage}`,
      logContext,
      error instanceof Error ? error : new Error(errorMessage)
    );
  } else if (severity === ErrorSeverity.MEDIUM) {
    logger.warn(
      `Warning in ${context?.operation || 'operation'}: ${errorMessage}`,
      logContext,
      error instanceof Error ? error : undefined
    );
  } else {
    logger.debug(`Error handled: ${errorMessage}`, logContext);
  }

  return handledError;
}

/**
 * Check if error should be shown to user (suppress 404s for empty lists, etc.)
 */
export function shouldShowError(error: HandledError, context?: ErrorContext): boolean {
  // Don't show 404 errors for list endpoints when database is empty
  if (
    error.category === ErrorCategory.NOT_FOUND &&
    context?.operation === 'list' &&
    error.statusCode === 404
  ) {
    return false;
  }

  // Always show authentication/authorization errors
  if (
    error.category === ErrorCategory.AUTHENTICATION ||
    error.category === ErrorCategory.AUTHORIZATION
  ) {
    return true;
  }

  // Show all other errors
  return true;
}

/**
 * Extract error message for toast notifications
 */
export function getErrorMessage(error: unknown, context?: ErrorContext): string {
  const handled = handleError(error, context);
  return handled.userMessage;
}

/**
 * Check if error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  const handled = handleError(error);
  return handled.retryable;
}







